import { useState, useContext, useEffect, useMemo, useRef } from "react";
import DataTable from "react-data-table-component";
import { differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';

import ViewSection from "./ViewSection";
import TableLoader from "./TableLoader";
import Modal from "./Modal";

import { AuthContext } from "../contexts/AuthContext";

import LabourerAllocationForm from "./LabourerAllocationForm";
import UserProfilePhoto from "./UserProfilePhoto";
import UploadLicenseForm from "./UploadLicenseForm";
import UploadDocumentForm from "./UploadDocumentForm";
import ConfirmPrompt from "./ConfirmPrompt";
import Loader from "./Loader";
import TimeEntryForm from "./TimeEntries/TimeEntryForm";
import GeneratedCredentialsDisplay from "./GeneratedCredentialsDisplay";

import useLabourer from '../hooks/useLabourer';
import useAllocations from "../hooks/useAllocations";
import useLeaveRequests from "../hooks/useLeaveRequests";
import useLicenses from "../hooks/useLicenses";
import useDocuments from "../hooks/useDocuments";
import useResume from "../hooks/useResume";

import licenseService from "../services/licenseService";
import documentService from "../services/documentService";
import resumeService from "../services/resumeService";
import authService from '../services/authService';
import labourerService from '../services/labourerService';
import timeEntryService from "../services/timeEntryService";
import projectService from "../services/projectService";

import generateTimeEntryColumns from "./TimeEntries/generateTimeEntryColumns";

import useTimeEntries from "../hooks/domain/timeEntries/useTimeEntries";
import { formatDateToSydneyTimeZone } from "../utils/dateUtils";

const LabourerView = ({ labourerUserId, onUpdateClicked, onDeleteClicked, onCloseClicked }) => {
  const { user } = useContext(AuthContext);

  const [allocationsPage, setAllocationsPage] = useState(1);
  const [allocationsPerPage, setAllocationsPerPage] = useState(10);

  const [projects, setProjects] = useState([]);

  const [selectedLicense, setSelectedLicense] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [isLicenseFormOpen, setIsLicenseFormOpen] = useState(false);
  const [isSubmittingLicense, setIsSubmittingLicense] = useState(false);
  const [isDocumentFormOpen, setIsDocumentFormOpen] = useState(false);
  const [isSubmittingDocument, setIsSubmittingDocument] = useState(false);
  const [isResumeFormOpen, setIsResumeFormOpen] = useState(false);
  const [isSubmittingResume, setIsSubmittingResume] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);
  const [isGeneratingCredentials, setIsGeneratingCredentials] = useState(false);
  const [isDownloadingInfo, setIsDownloadingInfo] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const actionsRef = useRef(null);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [isConfirmGenerateCredentialsOpen, setIsConfirmGenerateCredentialsOpen] = useState(false);

  const [labourerTimeEntriesPage, setLabourerTimeEntriesPage] = useState(1);
  const [labourerTimeEntriesPerPage, setLabourerTimeEntriesPerPage] = useState(5);

  const [selectedTimeEntry, setSelectedTimeEntry] = useState(null);

  const [isTimeEntryFormModalOpen, setIsTimeEntryFormModalOpen] = useState(false);
  const [isSubmittingTimeEntryForm, setIsSubmittingTimeEntryForm] = useState(false);

  const [isConfirmDeleteTimeEntryModalOpen, setIsConfirmDeleteTimeEntryModalOpen] = useState(false);

  const { data: labourerData, isLoading } = useLabourer({ userId: labourerUserId });

  const labourer = labourerData?.labourer;
  const labourerId = labourer?._id;

  const {
    data: allocationsData,
    isLoading: isLoadingAllocations,
    mutate: mutateAllocations
  } = useAllocations({ shouldFetch: !!labourer?._id, labourerId, page: allocationsPage, perPage: allocationsPerPage });

  const allocations = allocationsData?.allocations;
  const allocationCount = allocationsData?.count;

  const [leaveRequestsPage, setLeaveRequestsPage] = useState(1);
  const [leaveRequestsPerPage, setLeaveRequestsPerPage] = useState(10);

  const {
    data: leaveRequestsData,
    isLoading: isLoadingLeaveRequests,
  } = useLeaveRequests({ shouldFetch: !!labourer?._id, labourerId: labourerId, page: leaveRequestsPage, perPage: leaveRequestsPerPage });

  const leaveRequests = leaveRequestsData?.leaveRequests;
  const leaveRequestCount = leaveRequestsData?.count;

  const {
    data: licenseData,
    isLoading: isLoadingLicenses,
    mutate: mutateLicenses
  } = useLicenses({ shouldFetch: !!labourer?._id, labourerId: labourerId, page: 1, perPage: 1000 });

  const licenses = licenseData?.licenses;

  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    mutate: mutateDocuments
  } = useDocuments({ shouldFetch: !!labourer?._id, labourerId, page: 1, perPage: 1000 });

  const documents = documentsData?.documents;

  const {
    data: resumeData,
    isLoading: isLoadingResume,
    mutate: mutateResume
  } = useResume({ shouldFetch: !!labourer?._id, labourerId });

  const resume = resumeData?.resume;

  const [isAllocateLabourerOpen, setIsAllocateLabourerOpen] = useState(false);

  const onAllocateClicked = () => {
    setIsAllocateLabourerOpen(true);
  };

  const handleSendPasswordResetNotification = async () => {
    if (isSendingPasswordReset) return;

    setIsSendingPasswordReset(true);

    try {
      await authService.createForgotPasswordRequest({ userEmail: labourer.user.email });
      toast.success('Password reset email sent');
    } catch (error) {
      console.log(error);
      toast.error('Failed to send password reset email. Please try again later');
    } finally {
      setIsSendingPasswordReset(false);
    }
  };

  const handleGenerateCredentials = async () => {
    setIsConfirmGenerateCredentialsOpen(false);
    setIsGeneratingCredentials(true);

    try {
      const credentials = await labourerService.generateCredentials(labourerId);
      setGeneratedCredentials(credentials);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to generate credentials. Please try again.');
    } finally {
      setIsGeneratingCredentials(false);
    }
  };

  const handleCloseCredentialsModal = () => {
    setGeneratedCredentials(null);
  };

  const handleDownloadInfo = async () => {
    if (isDownloadingInfo) return;
    setIsDownloadingInfo(true);

    try {
      const blob = await labourerService.downloadInfo(labourerId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${labourer.user.fullName}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to download labourer info');
    } finally {
      setIsDownloadingInfo(false);
    }
  };

  const handleAllocationsPageChange = page => {
    setAllocationsPage(page);
  };

  const handleAllocationsPerRowsChange = async (newPerPage, page) => {
    setAllocationsPerPage(newPerPage);
    setAllocationsPage(page);
  };

  const handleAllocationCompleted = () => {
    setIsAllocateLabourerOpen(false);
    mutateAllocations();
  };

  const handleLeaveRequestsPageChange = page => {
    setLeaveRequestsPage(page);
  };

  const handleLeaveRequestsPerRowsChange = async (newPerPage, page) => {
    setLeaveRequestsPerPage(newPerPage);
    setLeaveRequestsPage(page);
  };

  const onAddLicenseClicked = () => {
    setIsLicenseFormOpen(true);
  };

  const onUpdateLicenseClicked = (license) => {
    setIsLicenseFormOpen(true);
    setSelectedLicense(license);
  };

  const handleLicenseUpload = async (payload) => {
    setIsSubmittingLicense(true);

    try {
      if (!selectedLicense) {
        await licenseService.createLicense({
          licenseData: {
            ...payload,
            labourer: labourerId
          },
        });
      }

      if (selectedLicense) {
        await licenseService.updateLicense({
          licenseId: selectedLicense._id,
          licenseData: payload
        });
      }

      await mutateLicenses();

      toast.success(selectedLicense ? 'License updated' : 'License added');

      setIsLicenseFormOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save license');
    } finally {
      setIsSubmittingLicense(false);
    }
  };

  const handleCloseLicenseForm = () => {
    setIsLicenseFormOpen(false);
    setSelectedLicense(null);
  };

  const onAddDocumentClicked = () => {
    setIsDocumentFormOpen(true);
  };

  const onUpdateDocumentClicked = (document) => {
    setIsDocumentFormOpen(true);
    setSelectedDocument(document);
  };

  const handleDocumentUpload = async (payload) => {
    setIsSubmittingDocument(true);

    try {
      if (!selectedDocument) {
        await documentService.createDocument({
          documentData: {
            ...payload,
            labourer: labourerId
          },
        });
      }

      if (selectedDocument) {
        await documentService.updateDocument({
          documentId: selectedDocument._id,
          documentData: payload
        });
      }

      await mutateDocuments();

      toast.success(selectedDocument ? 'Document updated' : 'Document added');

      setIsDocumentFormOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save document');
    } finally {
      setIsSubmittingDocument(false);
    }
  };

  const handleCloseDocumentForm = () => {
    setIsDocumentFormOpen(false);
    setSelectedDocument(null);
  };

  const handleResumeUpload = async (e) => {
    e.preventDefault();
    if (isSubmittingResume || !resumeFile) return;
    setIsSubmittingResume(true);

    const data = new FormData();
    data.append('labourer', labourerId);
    data.append('file', resumeFile);

    try {
      if (resume) {
        await resumeService.updateResume({ resumeId: resume._id, resumeData: data });
      } else {
        await resumeService.createResume({ resumeData: data });
      }
      await mutateResume();
      setIsResumeFormOpen(false);
      setResumeFile(null);
      toast.success('Resume uploaded');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to upload resume');
    } finally {
      setIsSubmittingResume(false);
    }
  };

  const handleTimeEntriesPageChange = (page) => {
    setLabourerTimeEntriesPage(page);
  };

  const handleTimeEntriesPerPageChange = (page, perPage) => {
    setLabourerTimeEntriesPage(page);
    setLabourerTimeEntriesPerPage(perPage);
  };

  const {
    timeEntries,
    count: timeEntryCount,
    isLoading: isLoadingTimeEntries,
    fetchTimeEntries,
  } = useTimeEntries({
    labourerId: labourerId,
    page: labourerTimeEntriesPage,
    perPage: labourerTimeEntriesPerPage,
    onError: () => toast.error('Something went wrong while loading time entries'),
  });

  const fetchProjects = async () => {
    const { projects } = await projectService.getProjects({ page: 1, perPage: 999 });
    setProjects(projects);
  }

  const handleEditTimeEntryClicked = (timeEntry) => {
    setSelectedTimeEntry(timeEntry);
    setIsTimeEntryFormModalOpen(true);
  };

  const handleRemoveTimeEntryClicked = (timeEntry) => {
    setSelectedTimeEntry(timeEntry);
    setIsConfirmDeleteTimeEntryModalOpen(true);
  };

  const handleSubmitTimeEntryForm = async (payload) => {
    setIsSubmittingTimeEntryForm(true);

    payload.labourerId = labourerId;
    payload.entryType = 'manual';

    try {
      if (selectedTimeEntry) {
        await timeEntryService.updateTimeEntry(selectedTimeEntry._id, payload);
      } else {
        await timeEntryService.createTimeEntry(payload);
      }

      await fetchTimeEntries();
      toast.success(selectedTimeEntry ? 'Time entry edited' : 'Time entry created');

      setIsTimeEntryFormModalOpen(false);
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong. Please try again');
    } finally {
      setIsSubmittingTimeEntryForm(false);
    }
  };

  const handleDeleteTimeEntry = async () => {
    try {
      await timeEntryService.removeTimeEntry(selectedTimeEntry._id);
      await fetchTimeEntries();

      setSelectedTimeEntry(null);
      setIsConfirmDeleteTimeEntryModalOpen(false);
      toast.success('Time entry removed.');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong while deleting the time entry. Please try again');
    }
  };

  const handleCloseTimeEntryFormModal = () => {
    setIsTimeEntryFormModalOpen(false);
    setSelectedTimeEntry(null);
  };

  useEffect(() => {
    if (labourerId) {
      fetchTimeEntries();
      fetchProjects();
    }
  }, [labourer, labourerId, labourerTimeEntriesPage, labourerTimeEntriesPerPage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setIsActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const projectSelectOptions = useMemo(() => {
    return projects.map(project => ({
      value: project._id,
      label: project.name
    }));
  }, [projects]);

  const columns = [
    {
      name: 'Name',
      selector: row => row.project.name
    },
    {
      name: 'Start Date',
      selector: row => formatDateToSydneyTimeZone(row.startDate)
    },
    {
      name: 'End Date',
      selector: row => formatDateToSydneyTimeZone(row.endDate)
    }
  ];

  const leaveRequestsColumns = [
    {
      name: 'Reason',
      selector: row => row.reason
    },
    {
      name: 'Status',
      selector: row => row.status
    },
    {
      name: 'Start Date',
      selector: row => formatDateToSydneyTimeZone(row.startDate)
    },
    {
      name: 'End Date',
      selector: row => formatDateToSydneyTimeZone(row.endDate)
    }
  ]

  const renderExpiresIn = row => {
    if (!row.expirationDate) return 'N/A';

    const daysUntilExpiry = differenceInDays(new Date(row.expirationDate), new Date());
    const colorClass = daysUntilExpiry <= 7 ? 'text-red-600' : daysUntilExpiry <= 30 ? 'text-amber-600' : '';

    return <span className={colorClass}>{daysUntilExpiry} days</span>;
  };

  const licenseColumns = [
    {
      name: 'HRWL',
      selector: row => row.type?.hrwlCode
    },
    {
      name: 'Date issued',
      selector: row => formatDateToSydneyTimeZone(row.dateIssued)
    },
    {
      name: 'Expiration Date',
      selector: row => formatDateToSydneyTimeZone(row.expirationDate)
    },
    {
      name: 'Expires in',
      cell: renderExpiresIn
    },
    {
      name: 'Actions',
      right: "true",
      cell: row => (
        <div className="flex gap-2">
          <button
            onClick={() => window.open(row.file, '_blank')}
            className="ase-button-primary text-xs px-2 py-1"
          >
            View
          </button>

          <button
            onClick={() => onUpdateLicenseClicked(row)}
            className="ase-button-primary text-xs px-2 py-1"
          >
            Update
          </button>
        </div>
      )
    }
  ]

  const documentsColumns = [
    {
      name: 'Document type',
      selector: row => row.type?.name
    },
    {
      name: 'Date Issued',
      selector: row => formatDateToSydneyTimeZone(row.dateIssued)
    },
    {
      name: 'Expiry Date',
      selector: row => formatDateToSydneyTimeZone(row.expirationDate)
    },
    {
      name: 'Expires in',
      cell: renderExpiresIn
    },
    {
      name: 'Actions',
      right: "true",
      cell: row => (
        <div className="flex gap-2">
          <button
            onClick={() => window.open(row.file, '_blank')}
            className="ase-button-primary text-xs px-2 py-1"
          >
            View
          </button>

          <button
            onClick={() => onUpdateDocumentClicked(row)}
            className="ase-button-primary text-xs px-2 py-1"
          >
            Update
          </button>
        </div>
      )
    }
  ]

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="bg-white rounded-lg border border-ase-blue w-full md:w-auto md:min-w-[900px]">
      <div className="flex items-center justify-between text-lg border-b bg-ase-blue text-white p-4 font-bold rounded-t-md">
        <div className="flex items-center gap-2">
          <UserProfilePhoto
            photoUrl={labourer?.user?.profilePicture}
            fullName={labourer?.user?.fullName}
          />

          <p>{labourer?.user?.fullName}</p>
        </div>

        <p
          onClick={onCloseClicked}
          className="font-bold cursor-pointer text-2xl"
        >
          X
        </p>
      </div>

      <div className="flex flex-col gap-5 border-b p-4">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-bold text-ase-blue uppercase tracking-wider">Personal</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 pt-1">
            <p className="text-sm"><span className="font-semibold text-gray-600">Preferred Name:</span> {labourer?.user?.preferredName || '—'}</p>
            <p className="text-sm"><span className="font-semibold text-gray-600">Date of Birth:</span> {labourer?.user?.dateOfBirth ? formatDateToSydneyTimeZone(labourer.user.dateOfBirth) : '—'}</p>
          </div>
        </div>

        <hr className="border-gray-200" />

        <div className="flex flex-col gap-1">
          <p className="text-xs font-bold text-ase-blue uppercase tracking-wider">Contact</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 pt-1">
            <p className="text-sm"><span className="font-semibold text-gray-600">Email:</span> {labourer?.user?.email || '—'}</p>
            <p className="text-sm"><span className="font-semibold text-gray-600">Phone:</span> {labourer?.user?.phoneNumber || '—'}</p>
            <p className="text-sm md:col-span-2"><span className="font-semibold text-gray-600">Residential Address:</span> {labourer?.user?.residentialAddress || '—'}</p>
          </div>
        </div>

        <hr className="border-gray-200" />

        <div className="flex flex-col gap-1">
          <p className="text-xs font-bold text-ase-blue uppercase tracking-wider">Employment</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 pt-1">
            <p className="text-sm"><span className="font-semibold text-gray-600">Onboarding Date:</span> {labourer?.onboardingDate ? formatDateToSydneyTimeZone(labourer.onboardingDate) : '—'}</p>
            <p className="text-sm"><span className="font-semibold text-gray-600">Offboarding Date:</span> {labourer?.offboardingDate ? formatDateToSydneyTimeZone(labourer.offboardingDate) : '—'}</p>
          </div>
        </div>

        <hr className="border-gray-200" />

        <div className='flex items-center gap-2'>
          <button
            onClick={onUpdateClicked}
            className='ase-button-primary text-sm'
          >
            Update
          </button>

          <div className='relative' ref={actionsRef}>
            {(() => {
              const loadingLabel = isSendingPasswordReset ? 'Sending...'
                : isGeneratingCredentials ? 'Generating...'
                : isDownloadingInfo ? 'Downloading...'
                : null;

              return (
                <>
                  <button
                    onClick={() => !loadingLabel && setIsActionsOpen(o => !o)}
                    disabled={!!loadingLabel}
                    className='ase-button-primary text-sm flex items-center gap-1'
                  >
                    {loadingLabel ?? 'Actions'}
                    {!loadingLabel && (
                      <span className={`transition-transform duration-150 ${isActionsOpen ? 'rotate-180' : ''}`}>▾</span>
                    )}
                  </button>

                  {isActionsOpen && (
                    <div className='absolute left-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1'>
                      <button
                        onClick={() => { handleSendPasswordResetNotification(); setIsActionsOpen(false); }}
                        className='w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
                      >
                        Send password reset
                      </button>

                      <button
                        onClick={() => { setIsConfirmGenerateCredentialsOpen(true); setIsActionsOpen(false); }}
                        className='w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
                      >
                        Generate credentials
                      </button>

                      <button
                        onClick={() => { handleDownloadInfo(); setIsActionsOpen(false); }}
                        className='w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
                      >
                        Download info
                      </button>

                      <hr className='my-1 border-gray-100' />

                      <button
                        onClick={() => { onDeleteClicked(); setIsActionsOpen(false); }}
                        className='w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50'
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      <ViewSection
        title="Allocations"
        actions={(
          <button
            onClick={onAllocateClicked}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'>
            Add
          </button>
        )}
        expandable={true}
        content={
          <DataTable
            columns={columns}
            data={allocations}
            highlightOnHover
            striped
            pointerOnHover
            pagination
            paginationServer
            progressPending={isLoadingAllocations}
            progressComponent={<TableLoader />}
            paginationTotalRows={allocationCount}
            paginationRowsPerPageOptions={[10, 20, 50]}
            paginationPerPage={allocationsPerPage}
            onChangePage={handleAllocationsPageChange}
            onChangeRowsPerPage={handleAllocationsPerRowsChange}
          />
        }
      />

      <ViewSection
        title="Licenses"
        actions={(
          <button
            onClick={onAddLicenseClicked}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'>
            Add
          </button>
        )}
        content={
          <DataTable
            columns={licenseColumns}
            data={licenses}
            striped
            highlightOnHover
            progressPending={isLoadingLicenses}
            progressComponent={<TableLoader />}
          />
        }
        expandable={true}
      />

      <ViewSection
        title="Documents"
        actions={(
          <button
            onClick={onAddDocumentClicked}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'>
            Add
          </button>
        )}
        content={
          <DataTable
            columns={documentsColumns}
            data={documents}
            striped
            highlightOnHover
            progressPending={isLoadingDocuments}
            progressComponent={<TableLoader />}
          />
        }
        expandable={true}
      />

      <ViewSection
        title="Resume"
        actions={!isLoadingResume && !resume && (
          <button
            onClick={() => setIsResumeFormOpen(true)}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'>
            Upload
          </button>
        )}
        expandable={true}
        content={
          isLoadingResume ? <TableLoader /> : resume ? (
            <div className="flex items-center justify-between p-4">
              <span className="text-sm font-medium">Resume on file</span>
              <div className="flex gap-2">
                <button
                  onClick={() => window.open(resume.file, '_blank')}
                  className="ase-button-primary text-xs px-2 py-1"
                >
                  View
                </button>
                <button
                  onClick={() => setIsResumeFormOpen(true)}
                  className="ase-button-primary text-xs px-2 py-1"
                >
                  Update
                </button>
              </div>
            </div>
          ) : (
            <p className="p-4 text-sm text-gray-500">No resume on file</p>
          )
        }
      />

      <ViewSection
        title="Leave Requests"
        expandable={true}
        content={
          <DataTable
            columns={leaveRequestsColumns}
            data={leaveRequests}
            highlightOnHover
            striped
            pointerOnHover
            pagination
            paginationServer
            progressPending={isLoadingLeaveRequests}
            progressComponent={<TableLoader />}
            paginationTotalRows={leaveRequestCount}
            paginationRowsPerPageOptions={[10, 20, 50]}
            paginationPerPage={leaveRequestsPerPage}
            onChangePage={handleLeaveRequestsPageChange}
            onChangeRowsPerPage={handleLeaveRequestsPerRowsChange}
          />
        }
      />

      <ViewSection
        title="Time entries"
        expandable={true}
        actions={(
          <button
            onClick={e => setIsTimeEntryFormModalOpen(true)}
            className='bg-white text-ase-blue font-semibold text-xs py-1 px-2 rounded-md'>
            Add
          </button>
        )}
        content={
          <DataTable
            columns={generateTimeEntryColumns({
              showLabourerField: false,
              onEditClicked: handleEditTimeEntryClicked,
              onRemoveClicked: handleRemoveTimeEntryClicked
            })}
            data={timeEntries}
            highlightOnHover
            striped
            pagination
            paginationServer
            progressPending={isLoadingTimeEntries}
            progressComponent={<TableLoader />}
            paginationTotalRows={timeEntryCount}
            paginationRowsPerPageOptions={[5, 10, 20, 50]}
            paginationPerPage={labourerTimeEntriesPerPage}
            onChangePage={handleTimeEntriesPageChange}
            onChangeRowsPerPage={handleTimeEntriesPerPageChange}
          />
        }
      />

      <ViewSection
        title="Emergency Contact"
        expandable={true}
        content={
          (
            <div className="flex flex-col gap-2 p-4">
              <p><b>Name</b>: {labourer?.emergencyContact?.fullName}</p>
              <p><b>Phone</b>: {labourer?.emergencyContact?.phoneNumber}</p>
              <p><b>Relationship</b>: {labourer?.emergencyContact?.relationship}</p>
            </div>
          )
        }
      />

      {user?.permissions?.includes('view-confidential-labourer-data') && (
        <>
          <ViewSection
            title="Super Annuation & Tax Details"
            expandable={true}
            content={
              (
                <div className="flex flex-col gap-2 p-4">
                  <p><b>Super Annuation Fund Name</b>: {labourer?.superAnnuation?.fundName}</p>
                  <p><b>Super Annuation ABN</b>: {labourer?.superAnnuation?.ABN}</p>
                  <p><b>Super Annuation USI</b>: {labourer?.superAnnuation?.USI}</p>
                  <p><b>Super Member Number</b>: {labourer?.superAnnuation?.superMemberNumber}</p>
                  <p><b>Tax File Number</b>: {labourer?.taxFileNumber}</p>
                </div>
              )
            }
          />

          <ViewSection
            title="Bank Details"
            expandable={true}
            content={
              (
                <div className="flex flex-col gap-2 p-4">
                  <p><b>Account name</b>: {labourer?.bankDetails?.accountName}</p>
                  <p><b>BSB</b>: {labourer?.bankDetails?.BSB}</p>
                  <p><b>Account number</b>: {labourer?.bankDetails?.accountNumber}</p>
                  <p><b>Bank name</b>: {labourer?.bankDetails?.bankName}</p>
                </div>
              )
            }
          />
        </>
      )}

      <Modal isOpen={isAllocateLabourerOpen}>
        <LabourerAllocationForm
          labourer={labourer}
          onAllocate={handleAllocationCompleted}
          onCloseClicked={() => setIsAllocateLabourerOpen(false)}
        />
      </Modal>

      <Modal isOpen={isLicenseFormOpen}>
        <UploadLicenseForm
          license={selectedLicense}
          licenseType={selectedLicense?.type}
          disabledLicenseTypes={selectedLicense ? [] : licenses?.filter(l => l.type != null).map(l => l.type._id)}
          isUploading={isSubmittingLicense}
          onSubmit={handleLicenseUpload}
          onCloseClicked={handleCloseLicenseForm}
        />
      </Modal>

      <Modal isOpen={isDocumentFormOpen}>
        <UploadDocumentForm
          document={selectedDocument}
          documentType={selectedDocument?.type}
          disabledDocumentTypes={selectedDocument ? [] : documents?.filter(doc => doc.type != null).map(doc => doc.type._id)}
          isUploading={isSubmittingDocument}
          onSubmit={handleDocumentUpload}
          onCloseClicked={handleCloseDocumentForm}
        />
      </Modal>

      <Modal isOpen={isTimeEntryFormModalOpen}>
        <TimeEntryForm
          title={selectedTimeEntry ? 'Edit Time Entry' : 'Add Time Entry'}
          shouldShowLabourerField={false}
          shouldShowProjectField={true}
          projectSelectOptions={projectSelectOptions}
          timeEntry={selectedTimeEntry}
          isLoading={isSubmittingTimeEntryForm}
          onSubmit={handleSubmitTimeEntryForm}
          onCloseClicked={handleCloseTimeEntryFormModal}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteTimeEntryModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete this time entry?`}
          onConfirm={handleDeleteTimeEntry}
          onCancel={() => setIsConfirmDeleteTimeEntryModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isResumeFormOpen}>
        <div className="bg-white rounded-lg border border-ase-blue w-full md:w-[500px]">
          <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
            <p>{resume ? 'Update Resume' : 'Upload Resume'}</p>
            <p onClick={() => { setIsResumeFormOpen(false); setResumeFile(null); }} className="font-bold cursor-pointer text-2xl">X</p>
          </div>
          <form onSubmit={handleResumeUpload} className="flex flex-col w-full">
            <div className="p-4 flex flex-col gap-4">
              <div>
                <label className="block mb-2 text-sm font-medium" htmlFor="admin_resume_file_input">Upload file</label>
                <input
                  type="file"
                  onChange={(e) => setResumeFile(e.target.files[0])}
                  required
                  id="admin_resume_file_input"
                  className="block p-4 w-full text-sm text-white rounded-lg cursor-pointer bg-ase-blue focus:outline-none ase-upload-input"
                />
              </div>
              <button type="submit" disabled={isSubmittingResume} className="ase-button-primary h-[48px] w-full flex items-center justify-center">
                {isSubmittingResume ? 'Uploading...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal isOpen={isConfirmGenerateCredentialsOpen}>
        <ConfirmPrompt
          message="This will immediately reset the labourer's current password. Make sure you are ready to share the new credentials with them."
          onConfirm={handleGenerateCredentials}
          onCancel={() => setIsConfirmGenerateCredentialsOpen(false)}
        />
      </Modal>

      <Modal isOpen={!!generatedCredentials}>
        {generatedCredentials && (
          <GeneratedCredentialsDisplay
            credentials={generatedCredentials}
            onClose={handleCloseCredentialsModal}
          />
        )}
      </Modal>
    </div>
  )
}

export default LabourerView;
